import Button, { type ButtonProps } from "@common/controls/Button/component";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormControlled } from "@common/controls/Form/component";
import { type FormModalControlledRenderParams } from "@common/controls/Modal/Modals/Form/component";
import { type FormSurvey, type GenericMap, System } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { EMPTY_OBJECT } from "@common/lib/consts";
import { i18nFormat } from "@common/lib/i18n";
import { Common } from "@common/types";
import React from "react";
import { getNextPage, getPageName } from "./lib";

import "./style.less";

export type FormSurveyChildrenProps = {
  previous?: () => void;
  next?: () => void;
  submit?: () => void;

  pageName: string | null;
  fields: Common.System.PageFormField[];
};
export type FormSurveyMode = "default" | "designer" | "preview";
type Props = {
  SecondaryButton?: React.ComponentType<ButtonProps>;
  PrimaryButton?: React.ComponentType<ButtonProps>;
  className?: string;
  data: FormSurvey;
  mode?: FormSurveyMode;
  onPageChange?: (pageId: string) => void;
  pageId?: string;

  mapFields: (
    fields: Common.System.PageFormField[],
    value: Common.GenericMap<any>,
    getKey: (x: Common.System.PageFormField) => string
  ) => any;

  proceed?: (
    page: Common.System.FormPage,
    pageData: GenericMap<GenericMap<any>>,
    leavingPageId: string
  ) => Promise<Common.System.FormPage | null>;
};

type State = {
  backPageIds: string[];
  pageData: GenericMap<GenericMap<any>>;
};

const FormSurveyComponent = ({ mode = "default", ...props }: Props) => {
  const i18n = useI18n();

  const [state, setState] = React.useState<State>({
    backPageIds: [],
    pageData: {}
  });

  const pages = React.useMemo(
    () => props.data.pages.filter((x) => x.formPageTypeKey === "Standard"),
    [props.data.pages]
  );

  const sortedPages = React.useMemo((): System.FormPage[] => sort(pages, { key: "sortOrder" }), [pages]);

  const [page, setPage] = React.useState<System.FormPage | null>(sortedPages[0]);

  React.useEffect(() => {
    const page = props.data.pages.find((x) => x.formPageId === props.pageId) ?? sortedPages[0];
    setPage(page?.formPageTypeKey === "Ending" && mode === "default" ? null : page);
  }, [props.pageId, props.data.pages, sortedPages, mode]);

  const fields = React.useMemo(
    () =>
      page
        ? sort(
            props.data.fields.filter((x) => x.formPageId === page.formPageId),
            { key: "sortOrder" }
          )
        : [],
    [props.data.fields, page]
  );

  const goTo = React.useCallback(
    (page: System.FormPage) => (setPage(page), props.onPageChange && props.onPageChange(page.formPageId)),
    [sortedPages, setPage, props.onPageChange]
  );

  const goBack = React.useCallback(() => {
    const backPageIds = state.backPageIds.slice();
    const goToPageId = backPageIds.pop()!;
    goTo(sortedPages.find((x) => x.formPageId === goToPageId)!);
    setState({ ...state, backPageIds });
  }, [goTo, state.backPageIds]);

  const proceed = React.useCallback(
    async (data: Common.GenericMap<any>) => {
      const pageData = {
        ...state.pageData,
        [page!.formPageId]: data
      };

      const nextPage = getNextPage(props.data, page!, pageData);
      if (!nextPage) throw new Error("BUG");

      const proceedInner = mode === "preview" ? () => Promise.resolve(nextPage) : props.proceed!;

      const next = await proceedInner(nextPage, pageData, page!.formPageId);
      if (next) {
        if (next.formPageTypeKey === "Standard") {
          setState({
            backPageIds: [...state.backPageIds, page!.formPageId],
            pageData
          });
        }

        goTo(next);
      }
    },
    [page, goTo, state, props.data, mode, props.proceed]
  );

  if (!props.data.fields.length && page?.formPageTypeKey !== "Ending") {
    return (
      <div className="form_survey">
        <div className="message">{i18n.formSurveyNoQuestionsInfo}</div>
      </div>
    );
  }

  if (!page) return null;

  const SecondaryButtonComponent = props.SecondaryButton ?? Button;
  const PrimaryButtonComponent = props.PrimaryButton ?? SubmitButton;

  return (
    <div className="form_survey">
      {page.formPageTypeKey === "Standard" ? (
        <>
          {mode !== "designer" && <h3 className="form_name">{props.data.form.name}</h3>}

          {!props.data.form.isSingle && (
            <>
              <h4 className="page_name">
                {getPageName(
                  i18n,
                  page.name,
                  sortedPages.findIndex((x) => x.formPageId === page.formPageId)
                )}
              </h4>
              {!!page.description && <p dangerouslySetInnerHTML={{ __html: page.description }}></p>}
            </>
          )}
          <FormControlled
            key={page.formPageId} // regenerate form to reset all data accumulated on the previous step
            defaultValues={state.pageData[page.formPageId] ?? EMPTY_OBJECT}
            requiredMode="star"
            submit={proceed}
          >
            {({ isSubmitting, submit, values }: FormModalControlledRenderParams<GenericMap<any>>) => {
              const nextPage = getNextPage(props.data, page, {
                ...state.pageData,
                [page!.formPageId]: values
              });

              const canSubmit = !nextPage || nextPage.formPageTypeKey === "Ending";
              return (
                <>
                  {props.mapFields(fields, values, (x) => x.formFieldId)}

                  <FormButtons align="left">
                    {!!state.backPageIds.length && (
                      <SecondaryButtonComponent
                        isDisabled={mode === "designer" || isSubmitting}
                        type="white"
                        onClick={goBack}
                        tooltip={mode === "designer" ? i18n.formSurveyActionDisabledTooltip : undefined}
                      >
                        {i18n.back}
                      </SecondaryButtonComponent>
                    )}
                    {canSubmit && mode !== "designer" ? (
                      <PrimaryButtonComponent isDisabled={isSubmitting} onClick={submit}>
                        {i18n.submit}
                      </PrimaryButtonComponent>
                    ) : (
                      <SecondaryButtonComponent
                        isDisabled={isSubmitting || mode === "designer"}
                        onClick={submit}
                        type="white"
                        tooltip={mode === "designer" ? i18n.formSurveyActionDisabledTooltip : undefined}
                      >
                        {i18n.next}
                      </SecondaryButtonComponent>
                    )}
                  </FormButtons>
                </>
              );
            }}
          </FormControlled>
        </>
      ) : (
        <div
          className="message"
          dangerouslySetInnerHTML={{
            __html: page.redirectUrl
              ? i18nFormat(i18n.formSurveyRedirectPreviewInfo, ["URL", page.redirectUrl])
              : page.description ?? ""
          }}
        ></div>
      )}
    </div>
  );
};
export default FormSurveyComponent;
